/**
 * Vendor Application Review — Staff only (Procurement)
 * Route: /app/procurement/vendor-applications
 */
import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';
import { CheckCircle2, XCircle, MessageSquare, ChevronRight, RefreshCw } from 'lucide-react';

const STATUS_BADGE = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-blue-50 text-blue-700',
  under_review: 'bg-amber-50 text-amber-700',
  clarification_needed: 'bg-orange-50 text-orange-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-red-50 text-red-600',
};

export default function VendorApplicationsReview() {
  const [apps, setApps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('submitted');
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [clarQuestion, setClarQuestion] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiGet('/procurement/vendor-applications', filterStatus ? { status: filterStatus } : {});
      setApps(d.items || []);
    } catch { toast.error('Failed to load applications'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterStatus]);

  const openDetail = async (app) => {
    setSelected(app);
    const d = await apiGet(`/procurement/vendor-applications/${app.id}`).catch(() => null);
    setDetail(d);
  };

  const approve = async () => {
    try {
      await apiPost(`/procurement/vendor-applications/${selected.id}/approve`, { notes: approveNotes });
      toast.success('Vendor approved — Vendor Master record created');
      setSelected(null); setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Enter a rejection reason'); return; }
    try {
      await apiPost(`/procurement/vendor-applications/${selected.id}/reject`, { reason: rejectReason });
      toast.success('Application rejected'); setSelected(null); setDetail(null); load();
    } catch (e) { toast.error('Failed'); }
  };

  const requestClar = async () => {
    if (!clarQuestion.trim()) { toast.error('Enter a question'); return; }
    try {
      await apiPost(`/procurement/vendor-applications/${selected.id}/request-clarification`, { question: clarQuestion });
      toast.success('Clarification request sent to vendor'); setClarQuestion('');
      const d = await apiGet(`/procurement/vendor-applications/${selected.id}`); setDetail(d);
    } catch (e) { toast.error('Failed'); }
  };

  if (selected && detail) return (
    <div className="space-y-5 max-w-4xl" data-testid="vendor-app-detail">
      <button onClick={() => { setSelected(null); setDetail(null); }}
        className="text-xs text-slate-500 hover:text-slate-800">← Back to applications</button>
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-bold text-xl">{detail.company_name}</h1>
          <div className="text-sm text-slate-500 mt-0.5">{detail.contact_name} · {detail.contact_email}</div>
        </div>
        <span className={`px-3 py-1 text-xs font-bold rounded-sm ${STATUS_BADGE[detail.status] || ''}`}>{detail.status?.replace(/_/g, ' ').toUpperCase()}</span>
      </div>
      {/* PQ responses */}
      {Object.entries(detail.pq_responses || {}).map(([sec, fields]) => (
        <div key={sec} className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="font-bold text-sm text-slate-900 mb-3 capitalize">{sec.replace(/_/g, ' ')}</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {Object.entries(fields || {}).map(([k, v]) => (
              <div key={k} className="flex gap-2 text-xs">
                <span className="text-slate-400 w-40 shrink-0 capitalize">{k.replace(/_/g, ' ')}</span>
                <span className="text-slate-700 font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      {/* Documents */}
      {(detail.uploaded_docs || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="font-bold text-sm mb-3">Uploaded Documents</div>
          <div className="space-y-1.5">
            {detail.uploaded_docs.map((d, i) => {
              // Use the backend serve endpoint — raw file_url is a storage path, not a public URL
              const viewUrl = `${process.env.REACT_APP_BACKEND_URL}/api/procurement/vendor-applications/${selected.id}/documents/${d.id}`;
              return (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 capitalize">{d.doc_type?.replace(/_/g, ' ')}</span>
                  <a href={viewUrl} target="_blank" rel="noreferrer" className="text-[#3b4872] font-bold hover:underline">View →</a>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Clarifications */}
      {detail.status in { submitted: 1, under_review: 1, clarification_needed: 1 } && (
        <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3">
          <div className="font-bold text-sm">Actions</div>
          {['submitted', 'under_review', 'clarification_needed'].includes(detail.status) && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Request Clarification</label>
                <div className="flex gap-2">
                  <input value={clarQuestion} onChange={e => setClarQuestion(e.target.value)} placeholder="What needs clarification?"
                    className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#3b4872]" />
                  <button onClick={requestClar} className="px-3 py-2 border border-[#3b4872] text-[#3b4872] rounded text-xs font-bold hover:bg-[#3b4872]/5 flex items-center gap-1">
                    <MessageSquare size={13} /> Send
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2 border-t">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Approval Notes</label>
                  <input value={approveNotes} onChange={e => setApproveNotes(e.target.value)} placeholder="Optional notes on approval"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none" />
                </div>
                <button onClick={approve} className="px-4 py-2 bg-emerald-600 text-white rounded text-xs font-bold self-end flex items-center gap-1.5" data-testid="approve-btn">
                  <CheckCircle2 size={13} /> Approve
                </button>
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Rejection Reason</label>
                  <input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Required"
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none" />
                </div>
                <button onClick={reject} className="px-4 py-2 bg-red-600 text-white rounded text-xs font-bold self-end flex items-center gap-1.5" data-testid="reject-btn">
                  <XCircle size={13} /> Reject
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      <PageHeader overline="Procurement" title="Vendor Applications"
        subtitle="Review and approve vendor pre-qualification applications" />
      <div className="flex items-center gap-3">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
          <option value="">All statuses</option>
          {['submitted', 'under_review', 'clarification_needed', 'approved', 'rejected', 'draft'].map(s => (
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <button onClick={load} className="p-2 border border-slate-300 rounded-sm text-slate-500 hover:bg-slate-50"><RefreshCw size={13} /></button>
      </div>
      {loading ? <div className="text-slate-400 py-8">Loading…</div> : apps.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">No applications with status "{filterStatus || 'any'}"</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm divide-y">
          {apps.map(app => (
            <button key={app.id} onClick={() => openDetail(app)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 text-left" data-testid={`app-row-${app.id}`}>
              <div>
                <div className="font-semibold text-sm">{app.company_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{app.contact_name} · {app.contact_email}</div>
                <div className="text-xs text-slate-400 mt-0.5">Submitted: {app.submitted_at?.slice(0,10) || '—'}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm ${STATUS_BADGE[app.status] || ''}`}>{app.status?.replace(/_/g, ' ')}</span>
                <ChevronRight size={15} className="text-slate-400" />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
